import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { AccountPanel } from "@/admin/AccountPanel";
import { ActivitiesPanel } from "@/admin/ActivitiesPanel";
import { ClubPanel } from "@/admin/ClubPanel";
import { LoginPanel } from "@/admin/LoginPanel";
import { PlayersPanel } from "@/admin/PlayersPanel";
import { PuzzlesPanel } from "@/admin/PuzzlesPanel";
import { RosterPanel } from "@/admin/RosterPanel";
import { Tetromino } from "@/components/Tetromino";
import { type AdminSession, adminApi } from "@/lib/adminApi";
import type { PieceType } from "@/game/pieces";

type TabId = "club" | "activities" | "roster" | "players" | "puzzles" | "account";

const TABS: readonly { id: TabId; label: string; piece: PieceType }[] = [
  { id: "club", label: "Club", piece: "J" },
  { id: "activities", label: "Meetings & events", piece: "L" },
  { id: "roster", label: "Roster", piece: "O" },
  { id: "players", label: "Players", piece: "S" },
  { id: "puzzles", label: "Puzzles", piece: "I" },
  { id: "account", label: "Account", piece: "T" },
];

/** Keeps the admin area out of search results even if the URL leaks. */
function useNoIndex(): void {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);

    const previousTitle = document.title;
    document.title = "Club admin · Tetris At UCI";
    return () => {
      tag.remove();
      document.title = previousTitle;
    };
  }, []);
}

export function Admin() {
  useNoIndex();

  const [session, setSession] = useState<AdminSession | null>(null);
  const [configured, setConfigured] = useState(true);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState<TabId>("club");

  /** Resume an existing session on load, so a refresh does not sign you out. */
  const checkSession = useCallback(async () => {
    setChecking(true);
    try {
      setSession(await adminApi.session());
    } catch {
      setSession(null);
      try {
        setConfigured((await adminApi.status()).configured);
      } catch {
        setConfigured(true);
      }
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const signOut = useCallback(async () => {
    try {
      await adminApi.logout();
    } finally {
      setSession(null);
    }
  }, []);

  // Any panel that hits a 401 mid-edit drops straight back to the login form.
  const handleExpired = useCallback(() => setSession(null), []);

  if (checking) {
    return (
      <main className="lattice flex min-h-screen items-center justify-center">
        <p className="meta opacity-60">Checking your session…</p>
      </main>
    );
  }

  if (!session) {
    return (
      <main className="lattice min-h-screen">
        <LoginPanel configured={configured} onSignedIn={() => void checkSession()} />
      </main>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b-2 border-ink bg-field/95 backdrop-blur-[2px]">
        <div className="mx-auto flex max-w-[68rem] flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
          <span className="display text-lg leading-none">Club admin</span>
          <span className="meta opacity-55">{session.username}</span>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/" className="meta px-2.5 py-1.5 hover:bg-ink hover:text-field">
              View site
            </Link>
            <button
              type="button"
              onClick={() => void signOut()}
              className="btn px-3 py-2 text-xs"
            >
              Sign out
            </button>
          </div>
        </div>

        <nav aria-label="Admin sections" className="mx-auto max-w-[68rem] px-5 pb-3">
          <div className="flex flex-wrap gap-2">
            {TABS.map((entry) => {
              const active = entry.id === tab;
              return (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setTab(entry.id)}
                  aria-current={active ? "page" : undefined}
                  className={`flex items-center gap-2 border-2 border-ink px-3 py-2
                    ${active ? "bg-ink text-field" : "bg-paper hover:bg-field"}`}
                >
                  <Tetromino type={entry.piece} cell={6} />
                  <span className="meta text-[0.625rem]">{entry.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="lattice min-h-[70vh]">
        <div className="mx-auto max-w-[68rem] px-5 py-8">
          {tab === "club" ? <ClubPanel onUnauthenticated={handleExpired} /> : null}
          {tab === "activities" ? <ActivitiesPanel onUnauthenticated={handleExpired} /> : null}
          {tab === "roster" ? <RosterPanel onUnauthenticated={handleExpired} /> : null}
          {tab === "players" ? <PlayersPanel onUnauthenticated={handleExpired} /> : null}
          {tab === "puzzles" ? <PuzzlesPanel onUnauthenticated={handleExpired} /> : null}
          {tab === "account" ? (
            <AccountPanel username={session.username} onSignedOut={handleExpired} />
          ) : null}
        </div>
      </main>
    </div>
  );
}
